import { Timestamp } from 'firebase/firestore';
import { FromFireStore, WithTimestamp } from '@/types/Firestore';
import { User } from 'firebase/auth';
import { LoaderFunction } from 'react-router-dom';
import { AuthLoaderFunction } from '@/types/auth';

export type PartialByKeys<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export interface Permission extends WithTimestamp {
  role: UserRole;
  addedBy: string;
  email: string;
  organizationId?: string;
  name: string;
  workspaceId?: string;
  subscriptionTier?: string;
}

export interface PermissionFromStore
  extends FromFireStore,
    Omit<Permission, 'createdAt'> {
  createdAt: Timestamp;
}

export type InvitationFromStore<Type extends 'workspace' | 'organization'> =
  Type extends 'workspace' ? WorkspaceInvitation : BaseInvitationFromFirestore;

interface BaseInvitationFromFirestore extends FromFireStore {
  invitedUserId: string;
  invitingUserId: string;
  invitedUserEmail: string;
  invitingUserEmail: string;
  createdAt: Timestamp;
  organizationId: string;
  organizationName: string;
  hash: string;
  name: string;
  role: UserRole;
}

export interface WorkspaceInvitation extends BaseInvitationFromFirestore {
  workspaceId: string;
  workspaceName: string;
  organizationId: string;
}

// export interface InvitationFromStore extends FromFireStore {
//   invitedUserId: string;
//   invitingUserId: string;
//   workspaceId: string;
//   invitedUserEmail: string;
//   invitingUserEmail: string;
//   createdAt: Timestamp;
//   workspaceName: string;
// }

export const permissions = {
  viewer: 1,
  dashboard_viewer: 2,
  editor: 3,
  admin: 4,
} as const;

export const userRoles = ['viewer', 'dashboard_viewer', 'editor', 'admin'] as const;
export type UserRoles = typeof userRoles;
export type UserRole = (typeof userRoles)[number];

export const subTiers = ['basic', 'essentials', 'advanced'];
export type SubscriptionTier = (typeof subTiers)[number];

export interface SubscriptionDetails {
  subscriptionTier: string;
  createdAt: Timestamp;
  productId: string;
  userEmail: string;
  userId: string;
  userName: string;
}

export interface SubscriptionDetailsFromStore
  extends FromFireStore,
    SubscriptionDetails {}
export interface WorkspaceSettingsLoaderData {
  permissions: PermissionFromStore[] | null;
  invitations: InvitationFromStore<'workspace'>[] | null;
  subscriptionDetails: SubscriptionDetailsFromStore | null;
}

export type TagsWithUserPermissions = {
  tags: TagFromStore[];
  userPermissions: PermissionFromStore;
};

export interface UserStateData {
  email: string | null;
  workspacePermission: PermissionFromStore | null;
  organizationPermission: PermissionFromStore | null;
  userId: null | string;
  isEmailVerified: boolean | null;
  canCreateOrganizations?: boolean;
}

export interface Tag {
  name: string;
}

export interface TagFromStore extends FromFireStore, WithTimestamp, Tag {}

export interface Keyword {
  keyword: string;
  tags: string[];
  language: string;
}

export interface KeywordFromStore extends FromFireStore, WithTimestamp, Keyword {}

export interface RawKeywordCSVData {
  keyword: string;
  language: string;
  tags: string;
}

export interface FetcherEmptyResponse {
  error?: { message: string };
}

export interface FetcherMultiErrorResponse {
  [key: string]: { message: string };
}

export type StoredUserData = Omit<
  User,
  'delete' | 'toJSON' | 'getIdToken' | 'getIdTokenResult' | 'reload'
>;

export interface StoredAuthData {
  fbase_key: string;
  value: StoredUserData;
}

export interface LoaderError {
  code: string;
  message: string;
  [key: string]: unknown;
}

export type LoaderData<TLoaderFn extends LoaderFunction | AuthLoaderFunction> = Awaited<
  ReturnType<TLoaderFn>
> extends Response | infer D
  ? D
  : never;

export type AuthLoaderData<TLoaderFn extends AuthLoaderFunction> = Awaited<
  ReturnType<TLoaderFn>
> extends Response | infer D
  ? D
  : never;
